import { math } from '@streamdown/math';
import type { Options } from 'rehype-sanitize';
import type { StreamdownProps } from 'streamdown';
import { defaultRehypePlugins } from 'streamdown';
import type { Pluggable, PluginTuple } from 'unified';

import { dataIdMap } from './remark-plugins/remark-constants';

import 'katex/dist/katex.min.css';

type HardenOptions = {
  allowDataImages?: boolean;
  allowedImagePrefixes?: string[];
  allowedLinkPrefixes?: string[];
  allowedProtocols?: string[];
  defaultOrigin?: string;
};

const DEFAULT_HARDEN_PLUGIN = defaultRehypePlugins.harden;
const DEFAULT_SANITIZE_PLUGIN = defaultRehypePlugins.sanitize;
if (!getIsHardenPluginTuple(DEFAULT_HARDEN_PLUGIN))
  throw new Error('Streamdown hardening plugin is not configurable.');
if (!getIsSanitizePluginTuple(DEFAULT_SANITIZE_PLUGIN))
  throw new Error('Streamdown sanitization plugin is not configurable.');

const DEFAULT_SCHEMA = DEFAULT_SANITIZE_PLUGIN[1] ?? {};
const ALINE_SCHEMA: Options = {
  ...DEFAULT_SCHEMA,
  attributes: {
    ...DEFAULT_SCHEMA.attributes,
    '*': [
      ...(DEFAULT_SCHEMA.attributes?.['*'] ?? []),
      ...Object.values(dataIdMap).flatMap(attribute => [
        attribute,
        attribute.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase()),
      ]),
    ],
    // KaTeX runs after sanitization and needs these remark-math markers.
    code: [['className', /^language-./, 'math-inline', 'math-display'], 'metastring'],
  },
  protocols: {
    ...DEFAULT_SCHEMA.protocols,
    href: [...(DEFAULT_SCHEMA.protocols?.href ?? []), 'mention'],
  },
  tagNames: [...(DEFAULT_SCHEMA.tagNames ?? []), 'u'],
};

export const ALINE_STREAMDOWN_PLUGINS = { math } satisfies StreamdownProps['plugins'];

const ALINE_SECURITY_PLUGINS = [
  [DEFAULT_SANITIZE_PLUGIN[0], ALINE_SCHEMA],
  [DEFAULT_HARDEN_PLUGIN[0], getAlineHardenOptions(DEFAULT_HARDEN_PLUGIN[1] ?? {})],
] satisfies NonNullable<StreamdownProps['rehypePlugins']>;

/** Sanitize after custom plugins restore the metadata used by product renderers. */
export function getAlineRehypePlugins(plugins?: StreamdownProps['rehypePlugins']) {
  return [...(plugins ?? [defaultRehypePlugins.raw]), ...ALINE_SECURITY_PLUGINS];
}

export function getAlineHardenOptions(defaultOptions: HardenOptions): HardenOptions {
  return {
    ...defaultOptions,
    allowedImagePrefixes: ['*'],
    allowedLinkPrefixes: ['*'],
    allowedProtocols: [...new Set([...(defaultOptions.allowedProtocols ?? []), 'mention:'])],
    defaultOrigin: undefined,
  };
}

function getIsHardenPluginTuple(plugin: Pluggable): plugin is PluginTuple<[HardenOptions]> {
  return Array.isArray(plugin) && typeof plugin[0] === 'function';
}

function getIsSanitizePluginTuple(plugin: Pluggable): plugin is PluginTuple<[Options]> {
  return Array.isArray(plugin) && typeof plugin[0] === 'function';
}
